import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Bot,
  GitBranch,
  Link2,
  Settings,
  HelpCircle,
  CreditCard,
} from 'lucide-react';
import { useAppStore } from '../../stores/appStore';

export function Sidebar() {
  const { t } = useTranslation();
  const { tenant } = useAppStore();
  const [showPartnerPlansModal, setShowPartnerPlansModal] = useState(false);

  const isPartnerTenant = !!tenant?.isPartner;
  const isPartnerManagedClient = !isPartnerTenant && !!tenant?.partnerTenantId;

  const navigation = [
    { key: 'dashboard', href: '/', icon: LayoutDashboard },
    { key: 'connections', href: '/connections', icon: Link2 },
    { key: 'bots', href: '/bots', icon: Bot },
    { key: 'flows', href: '/flows', icon: GitBranch },
    { key: 'plans', href: '/subscription', icon: CreditCard },
    { key: 'settings', href: '/settings', icon: Settings },
  ];

  return (
    <aside className="w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-slate-900 dark:text-slate-100">Chatbot Builder</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1">
        {navigation.map((item) => {
          const isPlansItem = item.key === 'plans';

          if (isPlansItem && isPartnerTenant) {
            return (
              <div
                key={item.key}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 dark:text-slate-500 cursor-not-allowed"
              >
                <item.icon className="w-5 h-5" />
                {t(`sidebar.navigation.${item.key}`)}
              </div>
            );
          }

          if (isPlansItem && isPartnerManagedClient) {
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setShowPartnerPlansModal(true)}
                className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
              >
                <item.icon className="w-5 h-5" />
                {t(`sidebar.navigation.${item.key}`)}
              </button>
            );
          }

          return (
            <NavLink
              key={item.key}
              to={item.href}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100',
                )
              }
            >
              <item.icon className="w-5 h-5" />
              {t(`sidebar.navigation.${item.key}`)}
            </NavLink>
          );
        })}
      </nav>

      {/* Help */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700">
        <a
          href="https://apidocs.bitrix24.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
        >
          <HelpCircle className="w-5 h-5" />
          {t('sidebar.docs')}
        </a>
      </div>

      {showPartnerPlansModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
          <div className="w-full max-w-md rounded-2xl border border-amber-200 bg-white p-6 shadow-2xl dark:border-amber-900/60 dark:bg-slate-900">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('sidebar.partnerPlansManagedTitle')}
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {t('sidebar.partnerPlansManagedDescription')}
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowPartnerPlansModal(false)}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
              >
                {t('sidebar.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
